/**
 * /admin — operator dashboard.
 *
 * Two panels:
 *   1. KPI cards from `/v1/admin/waitlist/stats` (total / pending /
 *      verified / verified-in-the-last-24h)
 *   2. Recent signups: the 8 newest waitlist rows from
 *      `/v1/admin/waitlist?limit=8`, linking through to the full
 *      AdminWaitlistPage
 *
 * Both fetches run in parallel on mount. The page renders the
 * skeleton immediately and fills in once promises resolve; if a
 * fetch fails (e.g. the operator's session expired), we render an
 * inline error band with a retry button — the rest of the shell
 * (sidebar / navbar) keeps working.
 *
 * Future tickets (OCP-1, OCP-23) will add a "recent tenants" panel
 * + "recent invite codes" panel using the same pattern.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { NotificationRow, type OperatorNotification } from './AdminNotificationsPage';
import { useAuth } from '../lib/auth-context';
import { fmtDate } from '../lib/format-time';

interface StatsResponse {
  total: number;
  byStatus: Record<string, number>;
  verifiedLast24h: number;
}

interface WaitlistListResponse {
  items: WaitlistItem[];
}

interface WaitlistItem {
  id: string;
  email: string;
  full_name: string;
  company: string | null;
  status: string;
  created_at: string;
  verified_at: string | null;
}

export function HomePage() {
  const { user } = useAuth();
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [recent, setRecent] = useState<WaitlistItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const [s, r] = await Promise.all([
        api<StatsResponse>('/v1/admin/waitlist/stats'),
        api<WaitlistListResponse>('/v1/admin/waitlist?limit=8'),
      ]);
      setStats(s);
      // The list endpoint is ordered ascending by created_at — reverse
      // for "newest first" display.
      setRecent([...r.items].reverse());
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Could not load dashboard data (HTTP ${err.status}).`);
      } else {
        setError('Could not load dashboard data.');
      }
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-8 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Welcome back, <span className="font-medium text-gray-900 dark:text-gray-100">{user?.email}</span>.
          </p>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-6 flex items-center justify-between rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs text-red-700 dark:text-red-300 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      <KpiGrid stats={stats} />

      <LatestUnreadNotifications />

      <section className="mt-10">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Recent waitlist signups</h2>
          <Link
            to="/waitlist"
            className="text-sm font-medium text-brand-500 hover:text-brand-600"
          >
            View all →
          </Link>
        </div>
        <RecentList items={recent} />
      </section>
    </div>
  );
}

function KpiGrid({ stats }: { stats: StatsResponse | null }) {
  const cards: { label: string; value: number | null; hint?: string }[] = [
    { label: 'Total signups', value: stats?.total ?? null },
    {
      label: 'Verified',
      value: stats?.byStatus.verified ?? null,
      hint: stats ? `${stats.byStatus.invited ?? 0} invited` : undefined,
    },
    {
      label: 'Pending verification',
      value: stats?.byStatus.pending_verification ?? null,
      hint: stats ? `${stats.byStatus.expired ?? 0} expired` : undefined,
    },
    { label: 'Verified · last 24 h', value: stats?.verifiedLast24h ?? null },
  ];
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm"
        >
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {c.label}
          </div>
          <div className="mt-2 text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
            {c.value === null ? <span className="text-gray-400">—</span> : c.value}
          </div>
          {c.hint && <div className="mt-1 text-xs text-gray-500">{c.hint}</div>}
        </div>
      ))}
    </div>
  );
}

function RecentList({ items }: { items: WaitlistItem[] | null }) {
  if (items === null) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        No signups yet. Once the public waitlist starts collecting entries
        they&apos;ll show up here.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-2">Email</th>
            <th className="px-4 py-2">Name</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2 text-right">Signed up</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {items.map((it) => (
            <tr key={it.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
              <td className="truncate px-4 py-2 font-medium text-gray-900 dark:text-gray-100">{it.email}</td>
              <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{it.full_name || '—'}</td>
              <td className="px-4 py-2">
                <StatusBadge status={it.status} />
              </td>
              <td className="px-4 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                {formatRelative(it.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending_verification: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 ring-amber-200',
    verified: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 ring-emerald-200',
    expired: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 ring-gray-200',
    invited: 'bg-brand-50 text-brand-700 ring-brand-200',
    signed_up: 'bg-sky-50 text-sky-700 ring-sky-200',
    declined: 'bg-rose-50 text-rose-700 ring-rose-200',
    unsubscribed: 'bg-gray-100 dark:bg-gray-800 text-gray-500 ring-gray-200',
    waiting: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-gray-200',
  };
  const cls = styles[status] ?? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-gray-200';
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const deltaSec = Math.round((now - then) / 1000);
  if (deltaSec < 60) return 'just now';
  const deltaMin = Math.round(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin} min ago`;
  const deltaH = Math.round(deltaMin / 60);
  if (deltaH < 24) return `${deltaH} h ago`;
  const deltaD = Math.round(deltaH / 24);
  if (deltaD < 7) return `${deltaD} d ago`;
  return fmtDate(iso);
}

/**
 * OCP-597 - the newest unread operator notifications, on the dashboard so
 * an alert is seen even when the device push was dismissed. Self-fetching so
 * the KPI load and this block fail independently.
 */
function LatestUnreadNotifications() {
  const [items, setItems] = useState<OperatorNotification[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await api<{ items: OperatorNotification[] }>('/v1/admin/notifications?unread=true&limit=5');
        if (!cancelled) setItems(r.items);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  if (!items || items.length === 0) return null;
  return (
    <section className="mt-10" data-testid="dashboard-unread-notifications">
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Unread notifications</h2>
        <Link to="/notifications" className="text-sm font-medium text-brand-500 hover:text-brand-600">
          Inbox
        </Link>
      </div>
      <ul className="divide-y divide-gray-100 dark:divide-gray-800 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4">
        {items.map((n) => <NotificationRow key={n.id} n={n} />)}
      </ul>
    </section>
  );
}
